import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowUp, ListCollapse, Maximize2, Minimize2, Printer, Share2, Type } from 'lucide-react';
import { useShortcuts, type Shortcut } from '~/lib/shortcuts';

/* De zwevende leesbalk uit de EJS-artikelpagina, met dezelfde zeven knoppen:
   inhoudsopgave, tekst kleiner, tekst groter, volledig scherm, afdrukken,
   delen en naar boven.

   TWEE DINGEN BEWUST ANDERS DAN HET ORIGINEEL:

   1. De sneltoetsen. De oude pagina bond Ctrl+T, Ctrl+0 en Ctrl+± — dat zijn
      "nieuw tabblad" en de browserzoom. Onderscheppen lukt in geen enkele
      browser betrouwbaar, dus die drie deden het half. Hier draaien ze zonder
      modifier (t, f, +, -, 0); useShortcuts laat losse letters niet vuren
      terwijl je in een tekstveld typt, dus het zoekveld in de kopbalk blijft
      gewoon werken.

   2. De lettergrootte schaalt --text-prose in plaats van de font-size van het
      hele artikel. Het origineel zette `fontSize: <n>rem` op .kb-article-body,
      waardoor élke em-maat eronder meeschoof — bij 180% werden de koppen
      onleesbaar groot en liep de code uit de kaders. */

const SCALE = { min: 0.8, max: 1.8, step: 0.1, default: 1 } as const;
const STORE_KEY = 'kb:font-scale'; // gedeeld met /admin/preview

const clamp = (n: number) => Math.min(SCALE.max, Math.max(SCALE.min, Math.round(n * 10) / 10));

export interface ReaderState {
  scale: number;
  tocVisible: boolean;
  fullscreen: boolean;
  shortcuts: Shortcut[];
  setScale: (fn: (n: number) => number) => void;
  resetScale: () => void;
  toggleToc: () => void;
  toggleFullscreen: () => void;
}

/** Leesvoorkeuren + de sneltoetsen die erbij horen. De pagina houdt de staat
 *  vast omdat zij de zijbalk verbergt en de volledig-schermklasse zet. */
export function useReader(): ReaderState {
  // Expliciet number: SCALE staat op `as const`, dus zonder dit leidt
  // TypeScript het letterlijke type 1 af en past er nooit iets anders in.
  const [scale, setScaleRaw] = useState<number>(SCALE.default);
  const [tocVisible, setTocVisible] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);

  // Pas ná het monteren lezen: localStorage bestaat niet tijdens SSR, en een
  // afwijkende beginwaarde zou een hydratatieverschil opleveren.
  useEffect(() => {
    const raw = Number(localStorage.getItem(STORE_KEY));
    if (raw) setScaleRaw(clamp(raw));
  }, []);

  const setScale = useCallback((fn: (n: number) => number) => {
    setScaleRaw((prev) => {
      const next = clamp(fn(prev));
      localStorage.setItem(STORE_KEY, String(next));
      return next;
    });
  }, []);

  const resetScale = useCallback(() => {
    setScaleRaw(SCALE.default);
    localStorage.removeItem(STORE_KEY);
  }, []);

  const toggleToc = useCallback(() => setTocVisible((v) => !v), []);
  const toggleFullscreen = useCallback(() => setFullscreen((v) => !v), []);

  // De body mag niet meescrollen achter het volledige scherm langs.
  useEffect(() => {
    if (!fullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [fullscreen]);

  const shortcuts = useMemo<Shortcut[]>(() => [
    {
      keys: 't', description: 'Inhoudsopgave tonen of verbergen',
      match: (e) => e.key === 't' && !e.ctrlKey && !e.metaKey && !e.altKey,
      run: toggleToc,
    },
    {
      keys: 'f', description: 'Volledig scherm aan of uit',
      match: (e) => e.key === 'f' && !e.ctrlKey && !e.metaKey && !e.altKey,
      run: toggleFullscreen,
    },
    {
      keys: '+', description: 'Tekst groter',
      match: (e) => (e.key === '+' || e.key === '=') && !e.ctrlKey && !e.metaKey,
      run: () => setScale((n) => n + SCALE.step),
    },
    {
      keys: '-', description: 'Tekst kleiner',
      match: (e) => e.key === '-' && !e.ctrlKey && !e.metaKey,
      run: () => setScale((n) => n - SCALE.step),
    },
    {
      keys: '0', description: 'Tekstgrootte terugzetten',
      match: (e) => e.key === '0' && !e.ctrlKey && !e.metaKey,
      run: resetScale,
    },
    {
      keys: 'Esc', description: 'Volledig scherm verlaten',
      match: (e) => e.key === 'Escape',
      run: () => setFullscreen(false),
      inDialog: true,
    },
  ], [toggleToc, toggleFullscreen, setScale, resetScale]);

  useShortcuts(shortcuts);

  return { scale, tocVisible, fullscreen, shortcuts, setScale, resetScale, toggleToc, toggleFullscreen };
}

/** De zwevende balk zelf. Verbergt zich bij naar beneden scrollen en komt
 *  terug zodra je omhoog scrollt of de muis beweegt — net als het origineel,
 *  maar zonder de timer die hem ook bij stilstand liet verdwijnen: een knop
 *  die uit zichzelf weggaat terwijl je hem zoekt, is geen knop. */
export function ReaderControls({
  reader, onShare,
}: {
  reader: ReaderState;
  onShare: () => void;
}) {
  const { scale, tocVisible, fullscreen, setScale, resetScale, toggleToc, toggleFullscreen } = reader;
  const [hidden, setHidden] = useState(false);
  const [atTop, setAtTop] = useState(true);

  useEffect(() => {
    let last = window.scrollY;
    let raf = 0;
    const update = () => {
      raf = 0;
      const y = window.scrollY;
      setAtTop(y < 200);
      if (Math.abs(y - last) > 60) {
        setHidden(y > last && y > 200);
        last = y;
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    const onMove = () => setHidden(false);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMove, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const pct = Math.round(scale * 100);

  return (
    <div
      // kb-no-print: deze balk hoort niet op het blad dat zijn eigen
      // printknop produceert.
      //
      // Onder lg ligt de balk PLAT onderaan het scherm, niet rechtop tegen de
      // rechterrand: rechtop is hij ±48px breed en dekt hij op een telefoon de
      // laatste woorden van elke tekstregel af (de tekstkolom is er maar 320
      // breed). Plat kost hij één strook onderaan, waar geen tekst staat.
      // marginBottom en niet bottom-[calc(...)]: bij een vast gepositioneerd
      // element dat aan `bottom` hangt schuift een ondermarge de doos precies
      // zoveel omhoog, en dat werkt in BEIDE oriëntaties van deze balk zonder
      // de -translate-x-1/2 aan te raken of twee breekpunten na te bouwen.
      // De variabele is 0px zolang er geen leerpadbalk staat.
      style={{ marginBottom: 'var(--kb-stepbar-h, 0px)' }}
      // z-170 in volledig scherm: het artikel wordt dan `fixed inset-0 z-200`
      // en dekte deze balk af — inclusief de knop waarmee je volledig scherm
      // weer verlaat. Buiten volledig scherm blijft het z-100, zodat de
      // chatknop (z-200) er gewoon overheen kan.
      className={`kb-no-print fixed bottom-3 left-1/2 flex -translate-x-1/2 flex-row gap-1 rounded-xl border border-hairline bg-raised/95 p-1.5 shadow-lg backdrop-blur-sm transition-opacity lg:right-4 lg:bottom-4 lg:left-auto lg:translate-x-0 lg:flex-col ${
        fullscreen ? 'z-210' : 'z-100'
      } ${hidden ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
      role="toolbar"
      aria-label="Leesinstellingen"
      aria-hidden={hidden}
    >
      <Btn
        label={tocVisible ? 'Inhoudsopgave verbergen (t)' : 'Inhoudsopgave tonen (t)'}
        pressed={!tocVisible}
        onClick={toggleToc}
        className="lg:flex hidden"
      >
        <ListCollapse size={16} />
      </Btn>

      <Btn label="Tekst groter (+)" onClick={() => setScale((n) => n + SCALE.step)} disabled={scale >= SCALE.max}>
        <Type size={17} />
      </Btn>
      <button
        type="button"
        onClick={resetScale}
        title="Tekstgrootte terugzetten (0)"
        aria-label={`Tekstgrootte ${pct} procent — klik om terug te zetten`}
        className="rounded-md px-1 py-0.5 text-2xs font-semibold text-ink-3 tabular-nums transition hover:bg-surface-hover hover:text-ink"
      >
        {pct}%
      </button>
      <Btn label="Tekst kleiner (-)" onClick={() => setScale((n) => n - SCALE.step)} disabled={scale <= SCALE.min}>
        <Type size={12} />
      </Btn>

      {/* scheiding: staand in de platte balk, liggend in de rechtopstaande */}
      <span className="my-1.5 w-px self-stretch bg-hairline lg:mx-1.5 lg:my-0 lg:h-px lg:w-auto" aria-hidden />

      <Btn label={fullscreen ? 'Volledig scherm verlaten (f)' : 'Volledig scherm (f)'} pressed={fullscreen} onClick={toggleFullscreen}>
        {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
      </Btn>
      <Btn label="Artikel afdrukken" onClick={() => window.print()}>
        <Printer size={16} />
      </Btn>
      <Btn label="Artikel delen" onClick={onShare}>
        <Share2 size={16} />
      </Btn>
      <Btn
        label="Naar boven"
        onClick={() => window.scrollTo({
          top: 0,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        })}
        disabled={atTop}
      >
        <ArrowUp size={16} />
      </Btn>
    </div>
  );
}

function Btn({ label, onClick, children, pressed, disabled, className = '' }: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  pressed?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled}
      className={`flex size-9 items-center justify-center rounded-md transition disabled:opacity-35 ${
        pressed ? 'bg-accent-soft text-accent-soft-ink' : 'text-ink-2 hover:bg-surface-hover hover:text-ink'
      } ${className}`}
    >
      {children}
    </button>
  );
}
